import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'

import { gsap, useGsap } from '@/lib/gsap'

/* GSAP interpolates between two resolved colours, so it cannot be handed a
   `var()`. Reading the token off the document once keeps the cursor in sync
   with the stylesheet without hardcoding the accent a second time. */
const readBrand = (): string => {
    const token = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-brand')
        .trim()

    return token.length > 0 ? token : '#0066FF'
}

/* Pointer-reactive cursor. A hard dot tracks the pointer 1:1 while a ring
   trails it on an eased quickTo, so direction changes bend the ring behind the
   dot. Elements opt in with data-cursor="link" | "view" | "drag" | "hide". */
export const Cursor: FC = (): ReactNode => {
    const dotRef = useRef<HTMLDivElement>(null)
    const ringRef = useRef<HTMLDivElement>(null)
    const labelRef = useRef<HTMLSpanElement>(null)
    const [enabled, setEnabled] = useState(false)

    useGsap(() => {
        const fine = window.matchMedia('(pointer: fine)').matches

        if (!fine) {
            return
        }

        setEnabled(true)

        const dot = dotRef.current
        const ring = ringRef.current
        const label = labelRef.current

        if (!dot || !ring || !label) {
            return
        }

        const brand = readBrand()

        gsap.set([dot, ring], { xPercent: -50, yPercent: -50, opacity: 0 })

        const dotX = gsap.quickTo(dot, 'x', { duration: 0.12, ease: 'power3' })
        const dotY = gsap.quickTo(dot, 'y', { duration: 0.12, ease: 'power3' })
        const ringX = gsap.quickTo(ring, 'x', { duration: 0.55, ease: 'power3' })
        const ringY = gsap.quickTo(ring, 'y', { duration: 0.55, ease: 'power3' })

        let visible = false

        const onMove = (event: PointerEvent): void => {
            if (!visible) {
                visible = true
                gsap.to([dot, ring], { opacity: 1, duration: 0.3 })
            }

            dotX(event.clientX)
            dotY(event.clientY)
            ringX(event.clientX)
            ringY(event.clientY)
        }

        const onLeave = (): void => {
            visible = false
            gsap.to([dot, ring], { opacity: 0, duration: 0.25 })
        }

        const applyState = (state: string | null, text: string): void => {
            if (state === 'view' || state === 'drag') {
                label.textContent = text
                gsap.to(ring, { width: 92, height: 92, backgroundColor: brand, borderColor: brand, duration: 0.45, ease: 'noma-soft' })
                gsap.to(dot, { scale: 0, duration: 0.3 })
                gsap.to(label, { opacity: 1, duration: 0.3, delay: 0.05 })

                return
            }

            if (state === 'link') {
                gsap.to(ring, { width: 54, height: 54, backgroundColor: 'rgba(252,252,250,0.12)', borderColor: 'rgba(252,252,250,0.5)', duration: 0.45, ease: 'noma-soft' })
                gsap.to(dot, { scale: 0.4, duration: 0.3 })
                gsap.to(label, { opacity: 0, duration: 0.2 })

                return
            }

            gsap.to(ring, { width: 34, height: 34, backgroundColor: 'rgba(252,252,250,0)', borderColor: 'rgba(252,252,250,0.35)', duration: 0.45, ease: 'noma-soft' })
            gsap.to(dot, { scale: 1, duration: 0.3 })
            gsap.to(label, { opacity: 0, duration: 0.2 })
        }

        const onOver = (event: PointerEvent): void => {
            const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-cursor]')

            applyState(
                target?.dataset.cursor ?? null,
                target?.dataset.cursorLabel ?? 'View'
            )
        }

        window.addEventListener('pointermove', onMove, { passive: true })
        window.addEventListener('pointerover', onOver, { passive: true })
        document.addEventListener('pointerleave', onLeave)

        return () => {
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('pointerover', onOver)
            document.removeEventListener('pointerleave', onLeave)
        }
    }, undefined, [])

    if (!enabled) {
        return null
    }

    return (
        <div className='pointer-events-none fixed inset-0 z-[130] hidden lg:block' aria-hidden='true'>
            <div
                ref={ringRef}
                className='absolute left-0 top-0 flex size-[34px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-paper/35 opacity-0 mix-blend-difference'
            >
                <span
                    ref={labelRef}
                    className='t-mono text-[10px] text-paper opacity-0'
                >
                    View
                </span>
            </div>

            <div
                ref={dotRef}
                className='absolute left-0 top-0 size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand opacity-0'
            />
        </div>
    )
}
